import {FormControl, InputLabel, MenuItem, Paper, Select} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs, {Dayjs} from "dayjs";
import {InvestigatorStat} from "../../../data/internal-affairs/ComplaintStatisticsResponse";

export interface StatisticsFilters {
    dateFrom: Dayjs | null
    dateTo: Dayjs | null
    investigatorId: string
    preset: string
}

const presets: { key: string; label: string; from: () => Dayjs; to: () => Dayjs }[] = [
    {key: "this_month",    label: "This month",     from: () => dayjs().startOf("month"), to: () => dayjs()},
    {key: "last_month",    label: "Last month",     from: () => dayjs().subtract(1, "month").startOf("month"), to: () => dayjs().subtract(1, "month").endOf("month")},
    {key: "last_3_months", label: "Last 3 months",  from: () => dayjs().subtract(3, "month").startOf("month"), to: () => dayjs().subtract(1, "month").endOf("month")},
    {key: "last_6_months", label: "Last 6 months",  from: () => dayjs().subtract(6, "month").startOf("month"), to: () => dayjs().subtract(1, "month").endOf("month")},
    {key: "last_year",     label: "Last year",      from: () => dayjs().subtract(1, "year").startOf("month"), to: () => dayjs().subtract(1, "month").endOf("month")},
    {key: "custom",        label: "Custom range",   from: () => dayjs().subtract(1, "month"), to: () => dayjs()},
]

export const defaultFilters: StatisticsFilters = {
    dateFrom: dayjs().subtract(1, "month").startOf("month"),
    dateTo: dayjs().subtract(1, "month").endOf("month"),
    investigatorId: "",
    preset: "last_month"
}

const TWO_YEARS_AGO = () => dayjs().subtract(2, "year")

export function buildStatsUrl(filters: StatisticsFilters): string {
    const p = new URLSearchParams()
    if (filters.dateFrom) p.set("dateFrom", filters.dateFrom.startOf("day").toISOString())
    if (filters.dateTo)   p.set("dateTo",   filters.dateTo.endOf("day").toISOString())
    if (filters.investigatorId) p.set("investigatorId", filters.investigatorId)
    return `/api/internal-affairs/statistics?${p.toString()}`
}

interface Props {
    filters: StatisticsFilters
    investigators: InvestigatorStat[]
    onChange: (f: StatisticsFilters) => void
}

export function StatisticsFilterBar({filters, investigators, onChange}: Props) {
    function handlePreset(key: string) {
        const p = presets.find(pr => pr.key === key)
        if (!p) return
        if (key === "custom") {
            onChange({...filters, preset: "custom"})
        } else {
            onChange({...filters, preset: key, dateFrom: p.from(), dateTo: p.to()})
        }
    }

    function handleDateFrom(v: Dayjs | null) {
        if (!v) return
        const clamped = v.isBefore(TWO_YEARS_AGO()) ? TWO_YEARS_AGO() : v
        const maxTo = clamped.add(2, "year")
        const newTo = filters.dateTo && filters.dateTo.isAfter(maxTo) ? maxTo : filters.dateTo
        onChange({...filters, preset: "custom", dateFrom: clamped, dateTo: newTo})
    }

    function handleDateTo(v: Dayjs | null) {
        if (!v) return
        const today = dayjs()
        const clamped = v.isAfter(today) ? today : v
        const minFrom = clamped.subtract(2, "year")
        const newFrom = filters.dateFrom && filters.dateFrom.isBefore(minFrom) ? minFrom : filters.dateFrom
        onChange({...filters, preset: "custom", dateTo: clamped, dateFrom: newFrom})
    }

    return (
        <Paper variant="outlined" sx={{p: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center"}}>
            <FormControl size="small" sx={{minWidth: 160}}>
                <InputLabel>Date Range</InputLabel>
                <Select value={filters.preset} label="Date Range"
                        onChange={e => handlePreset(e.target.value)}>
                    {presets.map(p => (
                        <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            {filters.preset === "custom" && (
                <>
                    <DatePicker label="From" value={filters.dateFrom}
                                onChange={handleDateFrom}
                                minDate={TWO_YEARS_AGO()}
                                maxDate={filters.dateTo ?? dayjs()}
                                format="DD/MM/YYYY"
                                slotProps={{textField: {size: "small", sx: {width: 155}}}}
                                views={["year","month","day"]}/>
                    <DatePicker label="To" value={filters.dateTo}
                                onChange={handleDateTo}
                                minDate={filters.dateFrom ?? TWO_YEARS_AGO()}
                                maxDate={dayjs()}
                                format="DD/MM/YYYY"
                                slotProps={{textField: {size: "small", sx: {width: 155}}}}
                                views={["year","month","day"]}/>
                </>
            )}
            <FormControl size="small" sx={{minWidth: 200}}>
                <InputLabel>Investigator</InputLabel>
                <Select value={filters.investigatorId} label="Investigator"
                        onChange={e => onChange({...filters, investigatorId: e.target.value})}>
                    <MenuItem value="">All investigators</MenuItem>
                    {investigators.map(i => (
                        <MenuItem key={i.communityId} value={i.communityId}>{i.nick}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Paper>
    )
}
